// src/components/admin/sections/AdminAboutUs.tsx
import React, { useState, useEffect } from 'react';
import apiElCristo, { AboutUsData } from '../../../service/api_elcristo';

const AdminAboutUs: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const [aboutData, setAboutData] = useState<AboutUsData>({
    subtitle: '',
    title: '',
    description: '',
    bulletPoints: [],
    exploreMoreLink: '',
    contactNumber: '',
    images: {
      bus: '',
      girl: '',
      dot: '',
      main: '',
      secondary: '',
      borderShape: ''
    }
  });

  const [bulletInput, setBulletInput] = useState({ text: '', url: '' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await apiElCristo.getAboutUsData();
      setAboutData(data);
    } catch (error) {
      console.error('Error loading about us:', error);
      setMessage({ type: 'error', text: 'Error al cargar la sección' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, section?: string, field?: string) => {
    const { value } = e.target;
    
    if (section && field) {
      setAboutData(prev => ({
        ...prev,
        [section]: {
          ...prev[section as keyof AboutUsData] as any,
          [field]: value
        }
      }));
    } else {
      const name = e.target.name || section;
      setAboutData(prev => ({
        ...prev,
        [name!]: value
      }));
    }
  };

  const handleAddBullet = () => {
    if (bulletInput.text.trim()) {
      setAboutData(prev => ({
        ...prev,
        bulletPoints: [...prev.bulletPoints, { text: bulletInput.text.trim(), url: bulletInput.url }]
      }));
      setBulletInput({ text: '', url: '' });
    }
  };

  const handleRemoveBullet = (index: number) => {
    setAboutData(prev => ({
      ...prev,
      bulletPoints: prev.bulletPoints.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      await apiElCristo.updateAboutUsData(aboutData);
      setMessage({ type: 'success', text: '✅ Sección actualizada correctamente' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      console.error('Error saving about us:', error);
      setMessage({ type: 'error', text: '❌ Error al guardar: ' + error.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="elcristo-admin-content">
        <div className="elcristo-loading-spinner">
          <div className="elcristo-spinner"></div>
          <p>Cargando sección...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="elcristo-admin-content">
      <div className="elcristo-content-header">
        <h1><i className="fas fa-info-circle"></i> Editar Sección Nosotros</h1>
        <p>Información sobre el colegio</p>
      </div>

      {message && (
        <div className={`elcristo-alert alert-${message.type}`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="elcristo-admin-form">
        {/* Main Content */}
        <div className="elcristo-form-section">
          <h3><i className="fas fa-heading"></i> Contenido Principal</h3>
          
          <div className="elcristo-form-row">
            <div className="elcristo-form-group">
              <label htmlFor="subtitle">Subtítulo</label>
              <input
                type="text"
                id="subtitle"
                name="subtitle"
                value={aboutData.subtitle}
                onChange={handleChange}
                placeholder="Ej: Sobre Nosotros"
              />
            </div>

            <div className="elcristo-form-group">
              <label htmlFor="title">Título</label>
              <input
                type="text"
                id="title"
                name="title"
                value={aboutData.title}
                onChange={handleChange}
                placeholder="Ej: Educación de Calidad"
              />
            </div>
          </div>

          <div className="elcristo-form-group">
            <label htmlFor="description">Descripción</label>
            <textarea
              id="description"
              name="description"
              value={aboutData.description}
              onChange={handleChange}
              placeholder="Descripción detallada sobre el colegio"
              rows={5}
            />
          </div>
        </div>

        {/* Bullet Points */}
        <div className="elcristo-form-section">
          <h3><i className="fas fa-list-ul"></i> Puntos Destacados</h3>
          
          <div className="elcristo-bullet-input-group">
            <input
              type="text"
              value={bulletInput.text}
              onChange={(e) => setBulletInput(prev => ({ ...prev, text: e.target.value }))}
              placeholder="Texto del punto destacado"
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddBullet())}
            />
            <input
              type="text"
              value={bulletInput.url}
              onChange={(e) => setBulletInput(prev => ({ ...prev, url: e.target.value }))}
              placeholder="URL (opcional)"
              style={{ maxWidth: '200px' }}
            />
            <button type="button" className="elcristo-btn-primary" onClick={handleAddBullet}>
              <i className="fas fa-plus"></i>
              Agregar
            </button>
          </div>

          <div className="elcristo-bullet-list">
            {aboutData.bulletPoints.map((bullet, index) => (
              <div key={index} className="elcristo-bullet-item">
                <i className="fas fa-check-circle"></i>
                <span>{bullet.text}</span>
                {bullet.url && (
                  <a href={bullet.url} target="_blank" rel="noopener noreferrer" className="elcristo-bullet-url">
                    <i className="fas fa-external-link-alt"></i>
                  </a>
                )}
                <button
                  type="button"
                  className="elcristo-btn-icon-small btn-delete"
                  onClick={() => handleRemoveBullet(index)}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            ))}

            {aboutData.bulletPoints.length === 0 && (
              <p className="elcristo-empty-text">No hay puntos destacados</p>
            )}
          </div>
        </div>

        {/* Contact Info */}
        <div className="elcristo-form-section">
          <h3><i className="fas fa-phone"></i> Información de Contacto</h3>
          
          <div className="elcristo-form-row">
            <div className="elcristo-form-group">
              <label htmlFor="contactNumber">Número de Contacto</label>
              <input
                type="text"
                id="contactNumber"
                name="contactNumber"
                value={aboutData.contactNumber}
                onChange={handleChange}
                placeholder="+56 9 4696 6778"
              />
            </div>

            <div className="elcristo-form-group">
              <label htmlFor="exploreMoreLink">Link "Explorar Más"</label>
              <input
                type="text"
                id="exploreMoreLink"
                name="exploreMoreLink"
                value={aboutData.exploreMoreLink}
                onChange={handleChange}
                placeholder="#nosotros"
              />
            </div>
          </div>
        </div>

        {/* Images */}
        <div className="elcristo-form-section">
          <h3><i className="fas fa-images"></i> Imágenes</h3>
          
          <div className="elcristo-form-row">
            <div className="elcristo-form-group">
              <label>Imagen Principal</label>
              <input
                type="text"
                value={aboutData.images.main}
                onChange={(e) => handleChange(e, 'images', 'main')}
                placeholder="/assets/kidsa/assets/img/about/02.jpg"
              />
            </div>

            <div className="elcristo-form-group">
              <label>Imagen Secundaria</label>
              <input
                type="text"
                value={aboutData.images.secondary}
                onChange={(e) => handleChange(e, 'images', 'secondary')}
                placeholder="/assets/kidsa/assets/img/about/02.png"
              />
            </div>
          </div>

          <div className="elcristo-form-row">
            <div className="elcristo-form-group">
              <label>Imagen Bus</label>
              <input
                type="text"
                value={aboutData.images.bus}
                onChange={(e) => handleChange(e, 'images', 'bus')}
                placeholder="/assets/kidsa/assets/img/about/bus.png"
              />
            </div>

            <div className="elcristo-form-group">
              <label>Imagen Niña</label>
              <input
                type="text"
                value={aboutData.images.girl}
                onChange={(e) => handleChange(e, 'images', 'girl')}
                placeholder="/assets/kidsa/assets/img/about/backpack.png"
              />
            </div>
          </div>

          <div className="elcristo-form-row">
            <div className="elcristo-form-group">
              <label>Imagen Dot</label>
              <input
                type="text"
                value={aboutData.images.dot}
                onChange={(e) => handleChange(e, 'images', 'dot')}
                placeholder="/assets/kidsa/assets/img/about/dot.png"
              />
            </div>

            <div className="elcristo-form-group">
              <label>Border Shape</label>
              <input
                type="text"
                value={aboutData.images.borderShape}
                onChange={(e) => handleChange(e, 'images', 'borderShape')}
                placeholder="/assets/kidsa/assets/img/about/border-shape-1.png"
              />
            </div>
          </div>
        </div>

        <div className="elcristo-form-actions">
          <button type="button" className="elcristo-btn-secondary" onClick={loadData} disabled={saving}>
            <i className="fas fa-undo"></i>
            Recargar
          </button>
          
          <button type="submit" className="elcristo-btn-primary" disabled={saving}>
            {saving ? (
              <>
                <span className="elcristo-spinner-small"></span>
                Guardando...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                Guardar Cambios
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminAboutUs;